import { Flame, Check, ChevronRight, Target } from "lucide-react";

/** Mini iPhone-style shell used by the two report-preview mockups. */
function MiniPhone({ children }) {
  return (
    <div className="relative bg-[#1c1c1e] rounded-[28px] p-1.5 shadow-[0_20px_45px_-15px_rgba(20,20,40,0.45)] ring-1 ring-black/20">
      <div className="relative bg-white rounded-[22px] overflow-hidden aspect-[9/18.5]">
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 z-20 w-[70px] h-[18px] bg-black rounded-full" aria-hidden="true">
          <span className="absolute right-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#222]" />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 w-0.5 h-0.5 rounded-full bg-emerald-400/80" />
        </div>
        <div className="pt-8 pb-3 px-3 bg-cream h-full flex flex-col">
          {children}
        </div>
      </div>
    </div>
  );
}

export function ReportInsightsPhone() {
  return (
    <MiniPhone>
      <p className="text-[8px] text-inkmid">Pista&apos;s Insights</p>
      <h4 className="font-heading text-[12px] leading-tight text-inkdark mb-2">Development Report</h4>

      <div className="bg-white rounded-xl shadow-soft border border-plum/5 px-2 py-2 flex items-center gap-2 mb-2">
        <div className="relative w-9 h-9 shrink-0">
          <svg viewBox="0 0 36 36" className="w-9 h-9 -rotate-90">
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="#E5E7EB" strokeWidth="3.5" />
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="#34A853" strokeWidth="3.5" strokeLinecap="round" strokeDasharray="84 100" pathLength="100" />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[8px] font-heading text-emerald-700">84%</span>
        </div>
        <div className="min-w-0">
          <p className="text-[9px] font-bold text-inkdark leading-tight">Overall Score</p>
          <p className="text-[8px] text-inkmid leading-tight">Above age-band median</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-soft border border-plum/5 px-2 py-2 space-y-1.5 mb-2">
        {[
          { label: "Fine Motor", pct: 82, color: "bg-mint" },
          { label: "Attention",  pct: 64, color: "bg-amber-400" },
          { label: "Social",     pct: 78, color: "bg-mint" },
          { label: "Emotion",    pct: 55, color: "bg-coral" },
          { label: "Language",   pct: 70, color: "bg-violet-500" },
        ].map((b) => (
          <div key={b.label}>
            <div className="flex justify-between text-[8px] font-semibold mb-0.5 text-inkdark"><span>{b.label}</span><span>{b.pct}%</span></div>
            <div className="h-1 bg-plum/10 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${b.color}`} style={{ width: `${b.pct}%` }} />
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl bg-violet-50 border border-violet-100 px-2 py-1.5 mb-2">
        <p className="text-[8px] font-bold text-violet-700 mb-0.5">Therapist Insight</p>
        <p className="text-[8px] text-inkdark leading-snug">Pista shows strong fine-motor skills. Focus next on emotion regulation through breathing games.</p>
      </div>

      <div className="mt-auto bg-white rounded-xl border border-plum/5 shadow-soft px-2 py-1.5 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded-full bg-mint/20 text-mint flex items-center justify-center"><Check className="w-2.5 h-2.5" strokeWidth={3} /></span>
          <p className="text-[8px] font-bold text-inkdark leading-tight">Therapist Verified</p>
        </div>
        <span className="text-[7px] text-inkmid">29 May</span>
      </div>
    </MiniPhone>
  );
}

export function SuggestedActivitiesPhone() {
  const acts = [
    { emoji: "🎈", title: "Balloon Volley",  sub: "Gross motor · 5 min" },
    { emoji: "🪡", title: "Pin Pickup",      sub: "Fine motor · 4 min" },
    { emoji: "🫁", title: "Belly Breathing", sub: "Emotion · 3 min" },
    { emoji: "🪞", title: "Mirror Game",     sub: "Social · 6 min" },
    { emoji: "🎲", title: "Story Dice",      sub: "Language · 7 min" },
  ];
  return (
    <MiniPhone>
      <p className="text-[8px] text-inkmid">Today for Pista</p>
      <h4 className="font-heading text-[12px] leading-tight text-inkdark mb-2">Suggested Activities</h4>

      <div className="rounded-xl bg-mint/15 border border-mint/30 px-2 py-1.5 flex items-center justify-between mb-2">
        <p className="text-[8px] font-bold text-mint-dark leading-tight">Great progress! 🎉</p>
        <p className="text-[8px] font-heading text-inkdark inline-flex items-center gap-1">12 <Flame className="w-2.5 h-2.5 inline text-orange-500" fill="currentColor" /></p>
      </div>

      <ul className="space-y-1.5 flex-1">
        {acts.map((a) => (
          <li key={a.title} className="bg-white rounded-xl border border-plum/5 shadow-soft px-2 py-1.5 flex items-center gap-1.5">
            <span className="text-base shrink-0">{a.emoji}</span>
            <div className="flex-1 min-w-0">
              <p className="text-[9px] font-bold text-inkdark leading-tight truncate">{a.title}</p>
              <p className="text-[7.5px] text-inkmid leading-tight truncate">{a.sub}</p>
            </div>
            <ChevronRight className="w-2.5 h-2.5 text-inkmid shrink-0" />
          </li>
        ))}
      </ul>

      <div className="mt-2 rounded-xl bg-coral/10 border border-coral/15 px-2 py-1.5 flex items-center gap-1.5">
        <Target className="w-3 h-3 text-coral shrink-0" strokeWidth={2.4} />
        <p className="text-[8px] text-inkdark leading-snug"><strong>95% match</strong> for Pista today</p>
      </div>
    </MiniPhone>
  );
}
